'use client';
import { useEffect, useState, useRef } from 'react';
import { Model } from '~/services/models.types';
import { CompletionStreamResponse } from '~/common/types';
import { Alert, AlertDescription, AlertTitle } from '~/components/ui/alert';
import { API_BASE_URL } from '~/config';
import { LineDecoder, SSEDecoder } from '~/lib/chat';
import { EmptyLLMOutput } from '../empty-llm-state';
import { LLMInput } from '../llm-input';
import { useRequestState } from '../request.context';
import CompletionResponse from './completion-response';
import { ChatConversation } from '~/components/ui/Chat';

export interface CompletionInterfaceProps {
  readonly apiKey: string;
  readonly model: Model<'text'>;
}
const controller = new AbortController();

export function CompletionInterface({ apiKey, model }: CompletionInterfaceProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [requestStatus, setRequestStatus] = useState<string | null>(null);
  const [output, setOutput] = useState<{
    content: string;
    metadata: {
      perplexity: number;
      totalTokens: number;
      averageTokenTime: number;
      firstTokenTime: number;
    };
  } | null>(null);

  const { completionState, setCompletionState, setSelectedTab } = useRequestState();

  const ref = useRef<HTMLDivElement | null>(null);

  function handleScroll() {
    ref?.current?.scrollIntoView({ block: 'start' });
  }

  useEffect(() => {
    setSelectedTab('completion');
  }, []);
  // eslint-disable-next-line complexity
  const fetchChatCompletion = async (text: string) => {
    if (!apiKey) {
      return;
    }

    try {
      setRequestStatus(null);
      setIsLoading(true);
      setCompletionState({ field: 'prompt', value: text });

      const startTime = Date.now();
      let firstTokenReceivedTime: number | undefined;
      let totalTokensReceived = 0;

      handleScroll();

      const response = await fetch(`${API_BASE_URL}/inference/v1/completions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'text/event-stream',
          Authorization: `Bearer ${apiKey}`,
        },
        signal: controller.signal,

        body: JSON.stringify({
          ...completionState,
          prompt: text,
          model: model.name,
          stream: true,
          n: 1,
          logprobs: 1,
        }),
      });

      if (response.status === 429) {
        setRequestStatus('over_quota');
        setIsLoading(false);
        return;
      }
      if (response.status !== 200) {
        const errorFromServer = await response.json().catch(() => null);
        const errorMessage = errorFromServer.error?.message;
        if (errorMessage === undefined) {
          setRequestStatus('unknown_error');
        } else {
          setRequestStatus(`${String(errorMessage)} To start a new conversation, please refresh the page`);
        }
        setIsLoading(false);

        return;
      }
      if (!response.body) {
        setRequestStatus('unknown_error');
        setIsLoading(false);
        return;
      }

      // Read the response as a stream of data
      const reader = response.body.getReader();
      const strDecoder = new TextDecoder('utf8');
      const decoder = new SSEDecoder();
      const lineDecoder = new LineDecoder();

      let buffer = '';

      while (true) {
        if (buffer.length === 0) {
          firstTokenReceivedTime = Date.now() - startTime;
        }
        // eslint-disable-next-line no-await-in-loop
        const { done, value } = await reader.read();
        if (done) {
          break;
        }
        // Massage and parse the chunk of data
        const lines = lineDecoder.decode(strDecoder.decode(value));

        // Clean up the data
        for (const line of lines) {
          const decodedSSE = decoder.decode(line);
          if (decodedSSE === null) {
            continue;
          }
          if (decodedSSE.data?.startsWith('{')) {
            const data: CompletionStreamResponse = JSON.parse(decodedSSE.data);
            buffer = `${buffer}${data.choices[0]?.text ?? ''}`;
            totalTokensReceived += data.choices[0]?.logprobs?.tokens.length ?? 0;

            const totalTime = Date.now() - startTime;
            const averageTime = totalTime / (totalTokensReceived - 1);

            setOutput({
              content: buffer,
              metadata: {
                // perplexity: data.choices[0]?.logprobs?. ?? 0,
                perplexity: 0,
                totalTokens: data.choices[0]?.logprobs?.tokens.length ?? 0,
                averageTokenTime: 1000 / averageTime,
                firstTokenTime: firstTokenReceivedTime || 0,
              },
            });
          }
        }
      }
    } catch (e: any) {
      if (e?.name !== 'AbortError') {
        // eslint-disable-next-line no-console
        console.error(e);
        setRequestStatus('unknown_error');
      }
    }
    setIsLoading(false);
  };

  return (
    <>
      <ChatConversation innerRef={ref}>
        {model.conversationConfig?.style === 'alpaca' && (
          <Alert color="warning" className="mr-4 mt-4">
            <AlertTitle>This model does not support multi-turn chat</AlertTitle>
            <AlertDescription>
              Please note that this model is trained using a single turn chat dataset, and will not work well with
              multi-turn chat. As such only the most recent message will be used.
            </AlertDescription>
          </Alert>
        )}
        {!isLoading && !output?.content.length && (
          <div className="mx-auto mt-8 md:pt-16">
            <EmptyLLMOutput model={model} apiMode={'completion'} />
          </div>
        )}
        {requestStatus ? (
          <Alert variant={'destructive'} className="mr-4">
            <AlertTitle>Error Generating response</AlertTitle>
            <AlertDescription>
              {requestStatus === 'over_quota'
                ? 'You have exceeded your max requests per hour, please try again later.'
                : requestStatus === 'unknown_error'
                  ? 'There was an error generating a response, please try again later.'
                  : `API Error: ${requestStatus}`}
            </AlertDescription>
          </Alert>
        ) : (
          (isLoading || output?.content.length) && (
            <CompletionResponse prompt={completionState.prompt} response={output} />
          )
        )}
      </ChatConversation>
      <LLMInput
        onSubmit={fetchChatCompletion}
        multiModal={model.tags.includes('Image')}
        isLoading={isLoading}
        onAbordRequest={() => {
          controller.abort();
          setIsLoading(false);
        }}
      />
    </>
  );
}
